import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session id", data: null });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null });
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", data, error: null });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data, error: null });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        setState({
          status: "error",
          data: null,
          error: e.response?.data?.detail || "Could not verify payment",
        });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      data-testid="checkout-success-page"
      className="bg-stone-950 text-stone-50 min-h-screen flex items-center justify-center px-6 pt-24 pb-12"
    >
      <div className="max-w-xl w-full border border-stone-800 bg-stone-900/40 p-10 sm:p-14 text-center">
        {state.status === "checking" && (
          <>
            <Loader2 size={40} className="text-amber-500 mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl font-bold">Verifying your payment…</h1>
            <p className="text-stone-400 mt-3">Hold tight, this only takes a second.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-amber-500 mx-auto mb-6" />
            <p className="text-overline text-amber-600 mb-3">Payment received</p>
            <h1 className="font-display text-4xl font-bold">Thank you.</h1>
            <p className="text-stone-300 mt-4 leading-relaxed">
              Your order for{" "}
              <span className="text-stone-50 font-semibold">
                {state.data?.product_name}
              </span>{" "}
              is confirmed. You'll get a shipping email at the address you used
              at checkout.
            </p>
            <Link
              to="/shop"
              data-testid="success-back-shop"
              className="inline-block mt-8 bg-amber-700 hover:bg-amber-600 text-stone-50 px-8 py-4 text-overline"
            >
              Back to shop →
            </Link>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout") && (
          <>
            <AlertTriangle size={48} className="text-amber-500 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold">
              {state.status === "timeout" ? "Still processing…" : "Payment didn't complete"}
            </h1>
            <p className="text-stone-400 mt-4">
              {state.status === "timeout"
                ? "Stripe is taking a moment. If you were charged, you'll get an email shortly."
                : "The session was cancelled or expired."}
            </p>
            <Link
              to="/shop"
              className="inline-block mt-8 border border-stone-700 hover:border-amber-500 text-stone-300 hover:text-amber-500 px-8 py-4 text-overline"
            >
              Back to shop
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <AlertTriangle size={48} className="text-amber-500 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold">Something went wrong</h1>
            <p className="text-stone-400 mt-4">{state.error}</p>
            <Link
              to="/shop"
              className="inline-block mt-8 border border-stone-700 hover:border-amber-500 text-stone-300 hover:text-amber-500 px-8 py-4 text-overline"
            >
              Back to shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
